import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';

/** Union of labor + production date columns (labor order first, then extras from production). */
function mergeTrackingDateLabels(laborDates, prodDates) {
  const a = laborDates || [];
  const b = prodDates || [];
  const seen = new Set();
  const out = [];
  for (const d of a) {
    if (d != null && d !== '' && !seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  for (const d of b) {
    if (d != null && d !== '' && !seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  return out;
}

/** Map per-row daily values onto unified date order; missing dates show as '--'. */
function alignSeriesToDateLabels(unifiedLabels, rowLabels, values) {
  if (!unifiedLabels.length) return [];
  const map = new Map();
  (rowLabels || []).forEach((label, i) => {
    if (label != null) map.set(label, values?.[i]);
  });
  return unifiedLabels.map((label) => (map.has(label) ? map.get(label) : '--'));
}

const getDateColumnBgClass = (index) => (index % 2 === 0 ? 'bg-[#F3F7FF]' : 'bg-white');

// Labor and Production share these widths so the date columns stay aligned.
// `availableWidth` is the table scrollport (after body zoom and modal insets),
// not window.innerWidth — PWA and iPad report a different viewport than the sheet.
// percent / used / total stay wide enough for "Installed" and "Estimated".
const getTrackingColumnWidths = (availableWidth) => {
  if (availableWidth < 520) {
    return {
      activity: 92,
      percent: 74,
      used: 82,
      total: 80,
    };
  }
  if (availableWidth < 760) {
    return {
      activity: 108,
      percent: 84,
      used: 92,
      total: 88,
    };
  }
  if (availableWidth < 1040) {
    return {
      activity: 140,
      percent: 100,
      used: 112,
      total: 108,
    };
  }
  if (availableWidth < 1280) {
    return {
      code: 72,
      activity: 136,
      percent: 104,
      used: 116,
      total: 108,
    };
  }
  return {
    code: 72,
    activity: 148,
    percent: 108,
    used: 124,
    total: 116,
  };
};

const hidesTrackingCodeColumn = (availableWidth) => !getTrackingColumnWidths(availableWidth).code;

const getTrackingColumnLeft = (availableWidth) => {
  const widths = getTrackingColumnWidths(availableWidth);
  if (hidesTrackingCodeColumn(availableWidth)) {
    const percent = widths.activity;
    const used = percent + widths.percent;
    return {
      activity: 0,
      percent,
      used,
      total: used + widths.used,
    };
  }
  const activity = widths.code;
  const percent = activity + widths.activity;
  const used = percent + widths.percent;
  return {
    code: 0,
    activity,
    percent,
    used,
    total: used + widths.used,
  };
};

const getEffectiveFixedColumnsWidth = (availableWidth) => {
  const widths = getTrackingColumnWidths(availableWidth);
  if (hidesTrackingCodeColumn(availableWidth)) {
    return widths.activity + widths.percent + widths.used + widths.total;
  }
  return widths.code + widths.activity + widths.percent + widths.used + widths.total;
};

const getTrackingFixedColumnStyle = (column, viewportWidth) => {
  const widths = getTrackingColumnWidths(viewportWidth);
  return {
    left: `${getTrackingColumnLeft(viewportWidth)[column]}px`,
    width: `${widths[column]}px`,
    minWidth: `${widths[column]}px`,
    maxWidth: `${widths[column]}px`,
  };
};

const getTrackingFixedTotalsStyle = (availableWidth) => {
  const widths = getTrackingColumnWidths(availableWidth);
  const width = hidesTrackingCodeColumn(availableWidth)
    ? widths.activity
    : widths.code + widths.activity;
  return {
    left: 0,
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
  };
};

const ModalTrackingDetails = ({ isOpen, onClose, rowData, projectCode }) => {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Scope name for DB lookup (Labor/PRODQTY descriptions for this scope)
  const scopeName = rowData?.scope ?? rowData?.dv_Type__c ?? rowData?.description ?? rowData?.name ?? '';

  // Fetch combined tracking details (Labor + Production) in single API call
  useEffect(() => {
    if (!isOpen || !rowData?.scopeId || !projectCode || !isReady) {
      setTrackingData(null);
      setError(null);
      return;
    }
    const base = process.env.REACT_APP_BACKEND_BASEURL;
    const path = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/tracking-details/${encodeURIComponent(rowData.scopeId)}`;
    const url = scopeName ? `${path}?scope=${encodeURIComponent(scopeName)}` : path;
    setLoading(true);
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(url, await getAuthConfig());
        if (cancelled) return;
        if (res.data?.status === 'success' && res.data?.data) {
          setTrackingData(res.data.data);
        } else {
          setTrackingData(null);
          setError(res.data?.message || 'No tracking data');
        }
      } catch (err) {
        if (cancelled) return;
        setTrackingData(null);
        setError(err.response?.data?.message || err.message || 'Failed to load tracking details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, rowData?.scopeId, projectCode, scopeName, isReady, getAuthConfig]);

  const unifiedDateLabels = useMemo(() => {
    const labor = trackingData?.laborTracking?.dateLabels || [];
    const prod = trackingData?.productionTracking?.dateLabels || [];
    return mergeTrackingDateLabels(labor, prod);
  }, [trackingData]);

  const trackingScrollRef = useRef(null);
  const trackingScrollbarRef = useRef(null);
  const trackingBodyRef = useRef(null);
  const trackingTouchStartRef = useRef(null);
  const trackingScrollWidthLast = useRef(0);
  const [trackingScrollWidth, setTrackingScrollWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1280
  ));
  const layoutWidth = trackingScrollWidth > 0 ? trackingScrollWidth : viewportWidth;
  const isMobile = hidesTrackingCodeColumn(layoutWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      trackingScrollWidthLast.current = 0;
      setTrackingScrollWidth(0);
      return;
    }
    const el = trackingScrollRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      // Avoid re-renders on subpixel / scrollbar flicker — those were shifting max scroll and felt like a “jump” at the end.
      if (Math.abs(w - trackingScrollWidthLast.current) < 1) return;
      trackingScrollWidthLast.current = w;
      setTrackingScrollWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, unifiedDateLabels.length, loading, error, trackingData]);

  if (!isOpen) return null;

  const heading =
    rowData?.scope ||
    rowData?.dv_Type__c ||
    rowData?.description ||
    rowData?.name ||
    'Production Performance – LWIC';

  // Labor Tracking data
  const laborTracking = trackingData?.laborTracking || {};
  const dateLabels = laborTracking.dateLabels || [];
  const rows = laborTracking.rows || [];
  const totals = laborTracking.totals;

  // Production Tracking data
  const productionTracking = trackingData?.productionTracking || {};
  const prodDateLabels = productionTracking.dateLabels || [];
  const prodRows = productionTracking.rows || [];
  const prodTotals = productionTracking.totals;

  // Wide enough for MM/DD/YYYY at tablet text size. Extra dates scroll instead of overlapping.
  const dateColPx = 112;
  const dateCellStyle = {
    width: `${dateColPx}px`,
    minWidth: `${dateColPx}px`,
    maxWidth: `${dateColPx}px`,
  };
  const columnWidths = getTrackingColumnWidths(layoutWidth);
  const effectiveFixedColumnsWidth = getEffectiveFixedColumnsWidth(layoutWidth);
  const trackingContentMinPx = Math.max(560, effectiveFixedColumnsWidth + unifiedDateLabels.length * dateColPx);

  const laborTotalsDailyAligned = alignSeriesToDateLabels(
    unifiedDateLabels,
    dateLabels,
    totals?.dailyHours || []
  );

  const prodTotalsDailyAligned = alignSeriesToDateLabels(
    unifiedDateLabels,
    prodDateLabels,
    prodTotals?.dailyQuantities || []
  );

  const trackingSectionBarStyle =
    trackingScrollWidth > 0 ? { width: trackingScrollWidth } : undefined;
  const trackingDateScrollbarWidth = Math.max(
    unifiedDateLabels.length * dateColPx,
    trackingContentMinPx - effectiveFixedColumnsWidth
  );

  const handleTrackingContentScroll = (event) => {
    const scrollbar = trackingScrollbarRef.current;
    if (!scrollbar) return;
    const nextScrollLeft = event.currentTarget.scrollLeft;
    if (Math.abs(scrollbar.scrollLeft - nextScrollLeft) < 1) return;
    scrollbar.scrollLeft = nextScrollLeft;
  };

  const handleTrackingScrollbarScroll = (event) => {
    const scrollContent = trackingScrollRef.current;
    if (!scrollContent) return;
    const nextScrollLeft = event.currentTarget.scrollLeft;
    if (Math.abs(scrollContent.scrollLeft - nextScrollLeft) < 1) return;
    scrollContent.scrollLeft = nextScrollLeft;
  };

  const isEventInFixedTrackingColumns = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientX - rect.left <= effectiveFixedColumnsWidth;
  };

  const handleTrackingWheelCapture = (event) => {
    if (!isEventInFixedTrackingColumns(event)) return;

    const body = trackingBodyRef.current;
    if (body && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      body.scrollTop += event.deltaY;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const handleTrackingTouchStartCapture = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const rect = event.currentTarget.getBoundingClientRect();
    trackingTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      startedInFixedColumns: touch.clientX - rect.left <= effectiveFixedColumnsWidth,
    };
  };

  const handleTrackingTouchMoveCapture = (event) => {
    const start = trackingTouchStartRef.current;
    const touch = event.touches?.[0];
    if (!start?.startedInFixedColumns || !touch) return;
    const deltaX = Math.abs(touch.clientX - start.x);
    const deltaY = Math.abs(touch.clientY - start.y);
    if (deltaX <= deltaY) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return createPortal(
    <div className="fixed inset-0 z-[10160] flex items-center justify-center bg-black/50">
      <div className="nettles-flow-sheet-panel absolute inset-1 flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:inset-4 md:inset-6 max-lg:top-[max(0.25rem,env(safe-area-inset-top))] max-lg:right-[max(0.25rem,env(safe-area-inset-right))] max-lg:bottom-[max(0.25rem,env(safe-area-inset-bottom))] max-lg:left-[max(0.25rem,env(safe-area-inset-left))]">
        {/* Header — spacing/typography aligned with Labor Tracking (ModalEnterData) */}
        <div className="relative z-10 flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3.5 shadow-[0_2px_5px_rgba(0,0,0,0.10)] rounded-t-xl sm:gap-4 sm:px-6 sm:py-[18px]">
          <h2 className="break-words pr-2 font-poppins text-[20px] font-bold leading-snug text-[#2C2C2C]">
            {heading}
          </h2>

          <button
            onClick={() => onClose(false)}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-gray-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              className="shrink-0"
              fill="none"
            >
              <path
                d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
                fill="#424143"
              />
            </svg>
          </button>
        </div>

        {/* Body: same outer gutters as Labor Tracking modal; table box inset like bordered labor grid */}
        {/* scrollbar-gutter: stable keeps inner width constant when the vertical scrollbar appears — prevents horizontal scroll “jump” at the end */}
        <div ref={trackingBodyRef} className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="px-1 pb-5 pt-3 sm:px-4 sm:pt-4 md:px-6 md:pb-6">
            <div className="relative overflow-hidden rounded-md border border-gray-300 bg-white">
              {(loading || error) && (
                <div className="flex min-h-[220px] items-center justify-center px-6 py-12">
                  {loading ? (
                    <div className="text-center font-poppins text-base text-gray-500">Loading tracking details...</div>
                  ) : (
                    <div className="text-center font-poppins text-base text-red-600">{error}</div>
                  )}
                </div>
              )}
              {!loading && !error && (
                <>
                <div
                  ref={trackingScrollRef}
                  onScroll={handleTrackingContentScroll}
                  onWheelCapture={handleTrackingWheelCapture}
                  onTouchStartCapture={handleTrackingTouchStartCapture}
                  onTouchMoveCapture={handleTrackingTouchMoveCapture}
                  className="min-w-0 w-full overflow-x-auto overscroll-x-none [container-type:inline-size] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  <div style={{ width: `${trackingContentMinPx}px`, minWidth: `${trackingContentMinPx}px` }}>
                <>
                  {/* Labor Tracking — sticky bar width = scrollport (cqw) so title does not move horizontally */}
                  <div
                    style={trackingSectionBarStyle}
                    className="sticky left-0 z-20 box-border w-[100cqw] max-w-none border-b border-gray-200 bg-[#F9FAFB] px-4 py-3 shadow-[1px_0_0_rgba(0,0,0,0.06)] md:px-6"
                  >
                    <h3 className="font-poppins text-base font-bold text-blackPrimary sm:text-[18px]">
                      Labor Tracking
                    </h3>
                  </div>

                  <table className="table-fixed text-[11px] sm:text-xs lg:text-sm" style={{ width: `${trackingContentMinPx}px`, minWidth: `${trackingContentMinPx}px` }}>
                    <colgroup>
                      {!isMobile && <col style={{ width: `${columnWidths.code}px` }} />}
                      <col style={{ width: `${columnWidths.activity}px` }} />
                      <col style={{ width: `${columnWidths.percent}px` }} />
                      <col style={{ width: `${columnWidths.used}px` }} />
                      <col style={{ width: `${columnWidths.total}px` }} />
                      {unifiedDateLabels.map((d) => (
                        <col key={`labor-col-${d}`} style={dateCellStyle} />
                      ))}
                    </colgroup>
                    <thead className="border-b border-gray-200 bg-white">
                      <tr className="text-gray-600 font-semibold">
                        {!isMobile && (
                          <th className="sticky z-30 bg-white py-2 px-1.5 text-left whitespace-nowrap" style={getTrackingFixedColumnStyle('code', layoutWidth)}>Code</th>
                        )}
                        <th className="sticky z-30 bg-white py-2 px-1.5 text-left" style={getTrackingFixedColumnStyle('activity', layoutWidth)}>Activity</th>
                        <th className="sticky z-30 bg-white py-2 px-1.5 text-center leading-tight sm:px-2" style={getTrackingFixedColumnStyle('percent', layoutWidth)}>
                          <span className="block whitespace-nowrap">% of Hrs</span>
                          <span className="block whitespace-nowrap">Used</span>
                        </th>
                        <th className="sticky z-30 bg-white py-2 px-1.5 text-center leading-tight sm:px-2" style={getTrackingFixedColumnStyle('used', layoutWidth)}>
                          <span className="block whitespace-nowrap">Hrs Used</span>
                          <span className="block whitespace-nowrap">to Date</span>
                        </th>
                        <th className="sticky z-30 border-r border-gray-200 bg-white py-2 px-1.5 text-center leading-tight sm:px-2 shadow-[2px_0_0_rgba(0,0,0,0.06)]" style={getTrackingFixedColumnStyle('total', layoutWidth)}>
                          <span className="block whitespace-nowrap">Total Hrs</span>
                          <span className="block whitespace-nowrap">Estimated</span>
                        </th>
                        {unifiedDateLabels.map((d, i) => (
                          <th key={d} className={`py-2.5 px-2 text-center whitespace-nowrap text-gray-500 font-medium ${getDateColumnBgClass(i)}`} style={dateCellStyle}>{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        const dailyAligned = alignSeriesToDateLabels(unifiedDateLabels, dateLabels, row.dailyHours || []);
                        return (
                          <tr key={idx} className="border-b border-gray-200">
                            {!isMobile && (
                              <td className="sticky z-10 bg-white py-2.5 px-1.5 text-left whitespace-nowrap" style={getTrackingFixedColumnStyle('code', layoutWidth)}>{row.code ?? '--'}</td>
                            )}
                            <td className="sticky z-10 bg-white py-2.5 px-1.5 text-left break-words" style={getTrackingFixedColumnStyle('activity', layoutWidth)}>{row.description ?? row.activity ?? '--'}</td>
                            <td className="sticky z-10 bg-white py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('percent', layoutWidth)}>{row.percentHrsUsed ?? '--'}</td>
                            <td className="sticky z-10 bg-white py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('used', layoutWidth)}>{row.hrsUsedToDate ?? '--'}</td>
                            <td className="sticky z-10 border-r border-gray-200 bg-white py-2.5 px-1 text-center whitespace-nowrap shadow-[2px_0_0_rgba(0,0,0,0.06)]" style={getTrackingFixedColumnStyle('total', layoutWidth)}>
                              {row.totalHrsEstimated !== '--' && !isNaN(parseFloat(row.totalHrsEstimated))
                                ? Math.round(parseFloat(row.totalHrsEstimated)).toLocaleString()
                                : row.totalHrsEstimated ?? '--'}
                            </td>
                            {dailyAligned.map((h, i) => (
                              <td key={i} className={`py-2.5 px-2 text-center whitespace-nowrap ${getDateColumnBgClass(i)}`} style={dateCellStyle}>{h}</td>
                            ))}
                          </tr>
                        );
                      })}
                      {totals && (
                        <tr className="font-semibold border-t border-b border-gray-200 bg-[#FAFBFC]">
                          <td colSpan={isMobile ? 1 : 2} className="sticky z-10 bg-[#FAFBFC] py-2.5 px-1.5 text-right whitespace-nowrap" style={getTrackingFixedTotalsStyle(layoutWidth)}>Totals</td>
                          <td className="sticky z-10 bg-[#FAFBFC] py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('percent', layoutWidth)}>
                            {totals.percentHrsUsed}
                          </td>
                          <td className="sticky z-10 bg-[#FAFBFC] py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('used', layoutWidth)}>
                            {totals.hrsUsedToDate}
                          </td>
                          <td className="sticky z-10 border-r border-gray-200 bg-[#FAFBFC] py-2.5 px-1 text-center whitespace-nowrap shadow-[2px_0_0_rgba(0,0,0,0.06)]" style={getTrackingFixedColumnStyle('total', layoutWidth)}>
                            {totals.totalHrsEstimated !== undefined && totals.totalHrsEstimated !== null && !isNaN(parseFloat(totals.totalHrsEstimated))
                              ? Math.round(parseFloat(totals.totalHrsEstimated)).toLocaleString()
                              : totals.totalHrsEstimated}
                          </td>
                          {laborTotalsDailyAligned.map((h, i) => (
                            <td key={i} className={`py-2.5 px-2 text-center whitespace-nowrap ${getDateColumnBgClass(i)}`} style={dateCellStyle}>
                              {h === '--' || h === null || h === undefined
                                ? '--'
                                : (Number.isFinite(Number(h)) ? (Math.round(Number(h) * 100) / 100) : '--')}
                            </td>
                          ))}
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Production Tracking — same sticky treatment as Labor section title */}
                  <div
                    style={trackingSectionBarStyle}
                    className="sticky left-0 z-20 box-border w-[100cqw] max-w-none border-b border-t border-gray-200 bg-[#F9FAFB] px-4 py-3 shadow-[1px_0_0_rgba(0,0,0,0.06)] md:px-6"
                  >
                    <h3 className="font-poppins text-base font-bold text-blackPrimary sm:text-[18px]">
                      Production Tracking
                    </h3>
                  </div>

                  <table className="table-fixed border-b border-gray-200 text-[11px] sm:text-xs lg:text-sm" style={{ width: `${trackingContentMinPx}px`, minWidth: `${trackingContentMinPx}px` }}>
                    <colgroup>
                      {!isMobile && <col style={{ width: `${columnWidths.code}px` }} />}
                      <col style={{ width: `${columnWidths.activity}px` }} />
                      <col style={{ width: `${columnWidths.percent}px` }} />
                      <col style={{ width: `${columnWidths.used}px` }} />
                      <col style={{ width: `${columnWidths.total}px` }} />
                      {unifiedDateLabels.map((d) => (
                        <col key={`prod-col-${d}`} style={dateCellStyle} />
                      ))}
                    </colgroup>
                    <thead className="border-b border-gray-200 bg-white">
                      <tr className="text-gray-600 font-semibold">
                        {!isMobile && (
                          <th className="sticky z-30 bg-white py-2 px-1.5 text-left whitespace-nowrap font-normal" style={getTrackingFixedColumnStyle('code', layoutWidth)}>{'\u00a0'}</th>
                        )}
                        <th className="sticky z-30 bg-white py-2 px-1.5 text-left" style={getTrackingFixedColumnStyle('activity', layoutWidth)}>Activity</th>
                        <th className="sticky z-30 bg-white py-2 px-1.5 text-center leading-tight sm:px-2" style={getTrackingFixedColumnStyle('percent', layoutWidth)}>
                          <span className="block whitespace-nowrap">%</span>
                          <span className="block whitespace-nowrap">Installed</span>
                        </th>
                        <th className="sticky z-30 bg-white py-2 px-1.5 text-center leading-tight sm:px-2" style={getTrackingFixedColumnStyle('used', layoutWidth)}>
                          <span className="block whitespace-nowrap">Installed</span>
                          <span className="block whitespace-nowrap">to Date</span>
                        </th>
                        <th className="sticky z-30 border-r border-gray-200 bg-white py-2 px-1.5 text-center leading-tight sm:px-2 shadow-[2px_0_0_rgba(0,0,0,0.06)]" style={getTrackingFixedColumnStyle('total', layoutWidth)}>
                          <span className="block whitespace-nowrap">Total</span>
                          <span className="block whitespace-nowrap">Estimated</span>
                        </th>
                        {unifiedDateLabels.map((d, i) => (
                          <th key={`prod-h-${d}`} className={`py-2.5 px-2 text-center whitespace-nowrap text-gray-500 font-medium ${getDateColumnBgClass(i)}`} style={dateCellStyle}>{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prodRows.map((row, idx) => {
                        const dailyAligned = alignSeriesToDateLabels(unifiedDateLabels, prodDateLabels, row.dailyQuantities || []);
                        return (
                          <tr key={idx} className="border-b border-gray-200">
                            {!isMobile && (
                              <td className="sticky z-10 bg-white py-2.5 px-1.5 text-left whitespace-nowrap text-gray-300" style={getTrackingFixedColumnStyle('code', layoutWidth)}>{'\u00a0'}</td>
                            )}
                            <td className="sticky z-10 bg-white py-2.5 px-1.5 text-left break-words" style={getTrackingFixedColumnStyle('activity', layoutWidth)}>{row.description ?? row.activity ?? '--'}</td>
                            <td className="sticky z-10 bg-white py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('percent', layoutWidth)}>{row.percentInstalled ?? '--'}</td>
                            <td className="sticky z-10 bg-white py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('used', layoutWidth)}>{row.installedToDate ?? '--'}</td>
                            <td className="sticky z-10 border-r border-gray-200 bg-white py-2.5 px-1 text-center whitespace-nowrap shadow-[2px_0_0_rgba(0,0,0,0.06)]" style={getTrackingFixedColumnStyle('total', layoutWidth)}>
                              {row.totalEstimated !== '--' && !isNaN(parseFloat(row.totalEstimated))
                                ? Math.round(parseFloat(row.totalEstimated)).toLocaleString()
                                : row.totalEstimated ?? '--'}
                            </td>
                            {dailyAligned.map((q, i) => (
                              <td key={i} className={`py-2.5 px-2 text-center whitespace-nowrap ${getDateColumnBgClass(i)}`} style={dateCellStyle}>{q}</td>
                            ))}
                          </tr>
                        );
                      })}
                      {prodTotals && (
                        <tr className="font-semibold border-t border-b border-gray-200 bg-[#FAFBFC]">
                          <td colSpan={isMobile ? 1 : 2} className="sticky z-10 bg-[#FAFBFC] py-2.5 px-1.5 text-right whitespace-nowrap" style={getTrackingFixedTotalsStyle(layoutWidth)}>Totals</td>
                          <td className="sticky z-10 bg-[#FAFBFC] py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('percent', layoutWidth)}>{prodTotals.percentInstalled}</td>
                          <td className="sticky z-10 bg-[#FAFBFC] py-2.5 px-1 text-center whitespace-nowrap" style={getTrackingFixedColumnStyle('used', layoutWidth)}>{prodTotals.installedToDate}</td>
                          <td className="sticky z-10 border-r border-gray-200 bg-[#FAFBFC] py-2.5 px-1 text-center whitespace-nowrap shadow-[2px_0_0_rgba(0,0,0,0.06)]" style={getTrackingFixedColumnStyle('total', layoutWidth)}>
                            {prodTotals.totalEstimated !== undefined && prodTotals.totalEstimated !== null && !isNaN(parseFloat(prodTotals.totalEstimated))
                              ? Math.round(parseFloat(prodTotals.totalEstimated)).toLocaleString()
                              : prodTotals.totalEstimated}
                          </td>
                          {prodTotalsDailyAligned.map((q, i) => (
                            <td key={i} className={`py-2.5 px-2 text-center whitespace-nowrap ${getDateColumnBgClass(i)}`} style={dateCellStyle}>{q}</td>
                          ))}
                        </tr>
                      )}
                    </tbody>
                  </table>

                </>
                  </div>
                </div>
                <div
                  style={trackingSectionBarStyle}
                  className="sticky bottom-0 z-30 box-border w-[100cqw] max-w-none border-t border-gray-200 bg-white"
                >
                  <div
                    ref={trackingScrollbarRef}
                    onScroll={handleTrackingScrollbarScroll}
                    className="h-4 overflow-x-auto overflow-y-hidden bg-stone-100 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-400"
                    style={{
                      marginLeft: `${effectiveFixedColumnsWidth}px`,
                      width: `calc(100% - ${effectiveFixedColumnsWidth}px)`,
                    }}
                  >
                    <div style={{ height: '1px', width: `${trackingDateScrollbarWidth}px` }} />
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalTrackingDetails;
